import { useMemo, type ReactNode } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight, faDownload } from '@fortawesome/free-solid-svg-icons'
import { cn } from '../shared/utils/cn'
import { useResponsiveLayout } from '../hooks/useResponsiveLayout'

// Message category definitions for API filtering
export type MessageCategory = 'deviceConfig' | 'sensorConfig' | 'testMessages' | 'schd' | 'snts'

export const MESSAGE_CATEGORY_CONFIG: Record<MessageCategory, { label: string; prefixes: string[]; color: string }> = {
  deviceConfig: { label: 'Device Config', prefixes: ['07', '08', '09', '0A', '0B'], color: 'bg-green-100 text-green-700 border-green-300' },
  sensorConfig: { label: 'Sensor Config', prefixes: ['0C'], color: 'bg-purple-100 text-purple-700 border-purple-300' },
  testMessages: { label: 'Test Messages', prefixes: ['04'], color: 'bg-red-100 text-red-700 border-red-300' },
  schd: { label: 'SCHD', prefixes: ['06'], color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  snts: { label: 'SNTS', prefixes: ['05'], color: 'bg-blue-100 text-blue-700 border-blue-300' },
}

export const ALL_MESSAGE_CATEGORIES: MessageCategory[] = ['deviceConfig', 'sensorConfig', 'testMessages', 'schd', 'snts']

// Helper to convert selected categories to API prefixes
export function categoriesToPrefixes(categories: Set<MessageCategory>): string[] {
  const prefixes: string[] = []
  categories.forEach((cat) => {
    prefixes.push(...MESSAGE_CATEGORY_CONFIG[cat].prefixes)
  })
  return prefixes
}

export type TimeRangePreset = 'day' | 'week' | 'month'

export interface TimeRange {
  preset: TimeRangePreset
  from: number | undefined // Unix timestamp in milliseconds
  to: number | undefined // Unix timestamp in milliseconds
  label: string
}

interface TimeRangeNavigationProps {
  currentRange: TimeRange
  onRangeChange: (range: TimeRange) => void
  maxDate?: Date
  className?: string
  // Chart action props (optional - only shown on Charts page)
  onRefresh?: () => void
  showBatteryCharts?: boolean
  onToggleBatteryCharts?: () => void
  showTemperatureCharts?: boolean
  onToggleTemperatureCharts?: () => void
  showTemperatureToggle?: boolean
  actionsEnabled?: boolean
  // Export action (optional - shown on Charts page)
  onExport?: () => void
  exportEnabled?: boolean
  // Extra actions rendered alongside Export (right side)
  extraActions?: ReactNode
  // Message category filter props (optional - for Messages page API filtering)
  selectedCategories?: Set<MessageCategory>
  onCategoriesChange?: (categories: Set<MessageCategory>) => void
  showCategoryFilters?: boolean
}

const DAY_MS = 24 * 3600 * 1000

const PRESET_CONFIGS = {
  day: { label: '1d', duration: DAY_MS },
  week: { label: '7d', duration: 7 * DAY_MS },
  month: { label: '28d', duration: 28 * DAY_MS },
} as const

const PRESET_ORDER: TimeRangePreset[] = ['day', 'week', 'month']

export function TimeRangeNavigation({
  currentRange,
  onRangeChange,
  maxDate = new Date(),
  className = '',
  onRefresh,
  showBatteryCharts,
  onToggleBatteryCharts,
  showTemperatureCharts,
  onToggleTemperatureCharts,
  showTemperatureToggle = false,
  actionsEnabled = false,
  onExport,
  exportEnabled = false,
  extraActions,
  selectedCategories,
  onCategoriesChange,
  showCategoryFilters = false,
}: TimeRangeNavigationProps) {
  const maxTimestamp = maxDate.getTime()
  const { isPhonePortrait, isTabletPortrait } = useResponsiveLayout()

  // Single row layout for: desktop, tablet landscape, phone landscape
  // Two row layout for: phone portrait, tablet portrait
  const useSingleRowLayout = !isPhonePortrait && !isTabletPortrait

  // Create preset range
  const createPresetRange = (preset: TimeRangePreset, referenceTime?: number): TimeRange => {
    const config = PRESET_CONFIGS[preset]

    let from: number
    let to: number

    if (preset === 'day') {
      // For "day" preset, use current day from 00:00:00 to 23:59:59
      const now = new Date()
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)

      from = startOfDay.getTime()
      to = endOfDay.getTime()
    } else {
      // For week and month presets, use the original duration-based logic
      to = referenceTime || maxTimestamp
      from = to - config.duration
    }

    // Calculate actual days for consistent labeling
    const days = calculateDays(from, to)
    const label = `${days} day${days !== 1 ? 's' : ''}`

    return {
      preset,
      from,
      to,
      label,
    }
  }

  // Navigation functions
  const navigateBackward = () => {
    if (!currentRange.from || !currentRange.to) return

    const duration = currentRange.to - currentRange.from
    const newTo = currentRange.from
    const newFrom = newTo - duration

    const newRange: TimeRange = {
      ...currentRange,
      from: newFrom,
      to: newTo,
    }

    onRangeChange(newRange)
  }

  const navigateForward = () => {
    if (!currentRange.from || !currentRange.to) return

    const duration = currentRange.to - currentRange.from
    const newFrom = currentRange.to
    const newTo = Math.min(newFrom + duration, maxTimestamp)

    // Don't allow navigation into the future
    if (newFrom >= maxTimestamp) return

    const newRange: TimeRange = {
      ...currentRange,
      from: newFrom,
      to: newTo,
    }

    onRangeChange(newRange)
  }

  const selectPreset = (preset: TimeRangePreset) => {
    const newRange = createPresetRange(preset)
    onRangeChange(newRange)
  }

  // Calculate days between two timestamps
  const calculateDays = (from: number, to: number) => {
    // Convert timestamps to dates to compare calendar days
    const fromDate = new Date(from)
    const toDate = new Date(to)

    // Set both dates to start of day for accurate day comparison
    const fromDay = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
    const toDay = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate())

    // Calculate difference in days (inclusive)
    const diffTime = toDay.getTime() - fromDay.getTime()
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1

    return diffDays
  }

  // Handle date picker changes
  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const [year, month, day] = event.target.value.split('-').map(Number)
    const selectedDate = new Date(year, month - 1, day, 0, 0, 0, 0)
    const newFrom = selectedDate.getTime()

    let newTo = currentRange.to

    // If end date is older than new start date, set end date to start date
    if (currentRange.to && newFrom > currentRange.to) {
      newTo = newFrom
    }

    if (newTo) {
      const days = calculateDays(newFrom, newTo)
      const newRange: TimeRange = {
        ...currentRange,
        preset: 'day',
        from: newFrom,
        to: newTo,
        label: `${days} day${days !== 1 ? 's' : ''}`,
      }
      onRangeChange(newRange)
    }
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const [year, month, day] = event.target.value.split('-').map(Number)
    const selectedDate = new Date(year, month - 1, day, 23, 59, 59, 999)
    const newTo = selectedDate.getTime()

    let newFrom = currentRange.from

    // If start date is newer than new end date, set start date to end date
    if (currentRange.from && newTo < currentRange.from) {
      newFrom = newTo
    }

    if (newFrom && newTo <= maxTimestamp) {
      const days = calculateDays(newFrom, newTo)
      const newRange: TimeRange = {
        ...currentRange,
        preset: 'day',
        from: newFrom,
        to: newTo,
        label: `${days} day${days !== 1 ? 's' : ''}`,
      }
      onRangeChange(newRange)
    }
  }

  // Which preset button lights up. Derived from the range rather than from
  // `currentRange.preset`, which the date pickers and the arrows leave pointing
  // at whichever button was pressed last — so a hand-picked 11-month range still
  // showed "1d" as selected. A preset only matches when the range is both the
  // right length and anchored to now; anything else leaves all three unselected.
  const activePreset = useMemo((): TimeRangePreset | null => {
    const { from, to } = currentRange
    if (!from || !to) return null
    if (Math.abs(to - maxTimestamp) > DAY_MS) return null

    const spanDays = Math.round((to - from) / DAY_MS)
    return PRESET_ORDER.find((preset) => Math.round(PRESET_CONFIGS[preset].duration / DAY_MS) === spanDays) ?? null
  }, [currentRange, maxTimestamp])

  // Days actually covered, for the label shown when no preset is selected.
  const rangeDays = useMemo(() => {
    const { from, to } = currentRange
    if (!from || !to) return 0
    return Math.max(1, Math.round((to - from) / DAY_MS))
  }, [currentRange])

  // Check if we can navigate forward (not already at present)
  const canNavigateForward = useMemo(() => {
    if (!currentRange.to) return false
    return currentRange.to < maxTimestamp - 3600 * 1000 // Allow some buffer (1 hour)
  }, [currentRange.to, maxTimestamp])

  // Format dates for input fields (use local date to avoid UTC shift)
  const formatDateForInput = (timestamp: number | undefined) => {
    if (!timestamp) return ''
    const d = new Date(timestamp)
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }

  // Get max date string for input
  const maxDateString = formatDateForInput(maxDate.getTime()) || ''

  return (
    <div className={cn(
      'flex items-center justify-between gap-2',
      className
    )}>
      {/* Left side: Action Buttons + Presets + Date Navigation */}
      <div className={cn(
        'flex gap-2',
        useSingleRowLayout
          ? 'flex-row flex-wrap items-center gap-1.5'
          : 'flex-col'
      )}>
        {/* Action Buttons + Presets */}
        <div className={cn(
          'flex items-center gap-1.5',
          useSingleRowLayout ? 'justify-start' : 'justify-between'
        )}>
          {/* Action Buttons */}
          {(onRefresh || onToggleBatteryCharts) && (
            <div className="flex items-center gap-1.5">
              {onRefresh && (
                <button
                  type="button"
                  disabled={!actionsEnabled}
                  onClick={onRefresh}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                    actionsEnabled
                      ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  )}
                >
                  Refresh
                </button>
              )}
              {onToggleBatteryCharts && (
                <button
                  type="button"
                  disabled={!actionsEnabled}
                  onClick={onToggleBatteryCharts}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                    !actionsEnabled
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : showBatteryCharts
                      ? 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  )}
                >
                  {showBatteryCharts ? 'Sensors' : 'Battery'}
                </button>
              )}
              {showTemperatureToggle && onToggleTemperatureCharts && (
                <button
                  type="button"
                  disabled={!actionsEnabled}
                  onClick={onToggleTemperatureCharts}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                    !actionsEnabled
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : showTemperatureCharts
                      ? 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  )}
                >
                  Temp.
                </button>
              )}
              {/* Divider after action buttons */}
              <div className="w-px h-5 bg-gray-200 mx-1" />
            </div>
          )}

          {/* Preset Buttons */}
          <div className="flex items-center gap-1">
            {PRESET_ORDER.map((preset) => (
              <button
                key={preset}
                onClick={() => selectPreset(preset)}
                className={cn(
                  'min-h-[32px] min-w-[40px]',
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  activePreset === preset
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                )}
              >
                {PRESET_CONFIGS[preset].label}
              </button>
            ))}
          </div>
        </div>

        {/* Date Navigation */}
        <div className={cn(
          'flex items-center gap-1',
          useSingleRowLayout ? 'justify-start' : 'justify-center'
        )}>
          {/* Previous Button */}
          <button
            onClick={navigateBackward}
            className="p-1.5 min-h-[32px] min-w-[32px] rounded transition-colors text-gray-500 hover:bg-gray-100 flex items-center justify-center"
            title="Previous period"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
          </button>

          {/* Date Pickers */}
          <input
            type="date"
            value={formatDateForInput(currentRange.from)}
            onChange={handleStartDateChange}
            max={formatDateForInput(currentRange.to)}
            className={cn(
              'min-h-[32px]',
              'px-2 py-1',
              'text-sm border border-blue-200 rounded',
              'bg-blue-50 text-blue-900 font-medium',
              'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent',
              'cursor-pointer'
            )}
            style={{ colorScheme: 'light' }}
          />

          <span className="text-gray-400 text-xs mx-0.5">→</span>

          <input
            type="date"
            value={formatDateForInput(currentRange.to)}
            onChange={handleEndDateChange}
            min={formatDateForInput(currentRange.from)}
            max={maxDateString}
            className={cn(
              'min-h-[32px]',
              'px-2 py-1',
              'text-sm border border-blue-200 rounded',
              'bg-blue-50 text-blue-900 font-medium',
              'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent',
              'cursor-pointer'
            )}
            style={{ colorScheme: 'light' }}
          />

          {/* Next Button */}
          <button
            onClick={navigateForward}
            disabled={!canNavigateForward}
            className={cn(
              'p-1.5 min-h-[32px] min-w-[32px] rounded transition-colors flex items-center justify-center',
              !canNavigateForward ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-100'
            )}
            title="Next period"
          >
            <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
          </button>

          {/* Duration Label - only show when custom range (not a preset) */}
          {activePreset === null && rangeDays > 0 && (
            <div className="text-xs text-blue-600 font-medium ml-1 whitespace-nowrap">
              {`${rangeDays}d`}
            </div>
          )}
        </div>

        {/* Message Category Filters - for API filtering */}
        {showCategoryFilters && selectedCategories && onCategoriesChange && (
          <>
            <div className="w-px h-5 bg-gray-200 mx-1" />
            <div className="flex items-center gap-1">
              {ALL_MESSAGE_CATEGORIES.map((category) => {
                const config = MESSAGE_CATEGORY_CONFIG[category]
                const isSelected = selectedCategories.has(category)
                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => {
                      const newCategories = new Set(selectedCategories)
                      if (isSelected) {
                        newCategories.delete(category)
                      } else {
                        newCategories.add(category)
                      }
                      onCategoriesChange(newCategories)
                    }}
                    className={cn(
                      'px-2 py-1 text-xs font-medium rounded-md border transition-colors whitespace-nowrap',
                      isSelected
                        ? config.color
                        : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100'
                    )}
                    title={`${isSelected ? 'Hide' : 'Show'} ${config.label} messages`}
                  >
                    {config.label}
                  </button>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Right side: Export Button. (Compare mode is toggled from the device
          list footer, not here.) */}
      <div className="flex items-center gap-2">
        {extraActions}
        {onExport && (
          <button
            type="button"
            disabled={!exportEnabled}
            onClick={onExport}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors flex items-center gap-1.5',
              exportEnabled
                ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            )}
          >
            <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />
            Export
          </button>
        )}
      </div>
    </div>
  )
}
